/* 필터·정렬·카테고리 집계. 화면 어디서도 이 규칙을 다시 쓰지 않게 한 곳에 모은다. */

import { reviewOf } from "./reviews.js";
import { isNew } from "./util.js";

/* 새로 들어온 곳을 부르는 말. 칩과 검색어가 같은 단어를 쓰게 한 곳에 둔다 —
   칩은 검색어를 채우는 손잡이일 뿐이라(App.jsx picked), 두 곳에 따로 적으면
   칩으로 건 필터를 검색창이 모르는 상태가 생긴다. */
export const NEW_TERM = "신규";

/* ' + ' 로 이어 붙인 검색어는 OR 다. '한식 + 카페'는 한식이거나 카페인 곳을 모두 낸다.
   AND 로 두면 카테고리 칩을 두 개 누른 순간 결과가 늘 0곳이다 — 한 가게의 category 는
   하나뿐이라 교집합이 비어 있다. 여러 개를 누르는 이유는 '이 중에 아무거나'이지
   '이걸 다 만족하는 곳'이 아니다. */
export function parseTerms(q) {
  return q.split("+").map((t) => t.trim()).filter(Boolean);
}

export function matchesQuery(store, terms, kakaoIds) {
  if (!terms.length) return true;
  // 카카오가 이 가게를 물어다 줬으면 통과. 메뉴·업종 키워드가 여기서 잡힌다.
  if (kakaoIds && store.kakao_place_id && kakaoIds.has(String(store.kakao_place_id))) return true;
  // 로컬 매칭은 네트워크를 안 타서 즉시 반응한다. 두 지도의 등록명을 모두 본다 —
  // 한쪽에만 있는 단어가 흔하다. '세로방'을 네이버는 '가정식집밥 세로방'으로 갖고 있어서
  // 네이버 이름을 빼면 '집밥'으로 못 찾는다.
  /* '신규'는 상호에도 주소에도 없는 말이라 검색거리에 직접 얹는다. 화면에 '신규'
     배지가 붙은 곳을 그 글자로 찾으려는 건 당연한 시도인데, 안 얹으면 0곳이 나온다.
     판정은 배지와 같은 isNew 하나를 본다. */
  const hay = `${store.name} ${store.category} ${store.kakao_name || ""} ${store.naver_name || ""} ${store.address || ""}`
    + (isNew(store) ? ` ${NEW_TERM}` : "");
  return terms.some((t) => hay.toLowerCase().includes(t));
}

/** 칩 하나를 검색어에 넣고 뺀다. 사용자가 직접 친 단어는 건드리지 않고 그대로 둔다. */
export function toggleTerm(q, label, on) {
  const rest = parseTerms(q).filter((t) => t.toLowerCase() !== label.toLowerCase());
  return (on ? [...rest, label] : rest).join(" + ");
}

function sorter(reviews, sort) {
  return (a, b) => {
    const ra = reviewOf(reviews, a.id), rb = reviewOf(reviews, b.id);
    switch (sort) {
      case "visits":
        return rb.visits.length - ra.visits.length || a.name.localeCompare(b.name, "ko");
      case "recent": {
        const da = ra.visits[0]?.date || "", db = rb.visits[0]?.date || "";
        return db.localeCompare(da) || a.name.localeCompare(b.name, "ko");
      }
      case "name":
        return a.name.localeCompare(b.name, "ko");
      default:
        return (a._dist ?? Infinity) - (b._dist ?? Infinity);
    }
  };
}

export function visible(stores, reviews, { q, lunchOnly, wantOnly, sort, kakaoIds }) {
  const needles = parseTerms(q.toLowerCase());
  return stores
    .filter((s) => {
      // 수집한 데이터 값으로 목록에서 빼는 건 '점심만' 하나뿐이다. 데이터 값 하나로
      // 가게가 지도에서 통째로 사라지면, 그 값이 틀렸을 때 틀린 줄도 모른다 —
      // 맥도날드가 그렇게 파싱 오류 하나로 안 보였다. 나머지는 흐리게 보여주되 지우지 않는다.
      if (lunchOnly && s.lunch_candidate === false) return false;
      // 찜은 위 규칙의 예외다. 스크래핑한 값이 아니라 사용자가 직접 켠 값이라,
      // 틀릴 수가 없고 안 보이는 것이 곧 의도다 — 내가 표시한 곳만 보겠다고 누른 것이다.
      if (wantOnly && !reviewOf(reviews, s.id).want_to_go) return false;
      return matchesQuery(s, needles, kakaoIds);
    })
    .sort(sorter(reviews, sort));
}

// 카카오맵 '주변 탐색'처럼 자주 쓰는 키워드를 바로 누르게 한다.
// 실제 데이터에 있는 카테고리에서 뽑으므로 눌렀는데 0곳인 배지는 안 생긴다.
const CAT_LABEL = {
  "카페/베이커리/패스트푸드": "카페",
  "아시안푸드": "아시안",
  "편의점/마트": "편의점",
};

/* '신규' 칩에 붙는 수. categories 와 같은 규칙으로 센다 — 기본 화면이 '점심만'이라
   편의점까지 세면 칩에 적힌 수와 눌러서 나오는 수가 어긋난다.
   0이면 칩을 안 그린다(App.jsx). new_stores.json 이 비면 0이 되는 값이라, 안 그러면
   눌러도 늘 0곳인 죽은 칩이 화면에 남는다. */
export function countNew(stores) {
  return stores.filter((s) => s.lunch_candidate !== false && isNew(s)).length;
}

export function categories(stores) {
  const counts = new Map();
  for (const s of stores) {
    if (s.lunch_candidate === false) continue;   // 기본 화면('점심만')과 숫자를 맞춘다
    const label = CAT_LABEL[s.category] || s.category;
    if (label) counts.set(label, (counts.get(label) || 0) + 1);
  }
  return [...counts].sort((a, b) => b[1] - a[1]).map(([label, n]) => ({ label, n }));
}
